/**
 * SemanticSearch — displays semantic search results from the AI vector index.
 * Shown as an alternate mode in the search UI when the user toggles "Semantic" search.
 * Shows page title, similarity score, library name, and a KU text snippet.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pagesApi from '../../api/pages-api';
import type { SemanticSearchResult } from '../../api/pages-api';

interface SemanticSearchProps {
  readonly query: string;
  readonly libraryId?: string;
  readonly onClose?: () => void;
}

export function SemanticSearch({ query, libraryId, onClose }: SemanticSearchProps): React.ReactElement {
  const navigate = useNavigate();
  const [results, setResults] = useState<SemanticSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.searchSemantic(query, {
          k: 10,
          libraryId,
        });
        if (!cancelled) {
          setResults(response.data ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Semantic search failed');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    search();
    return () => { cancelled = true; };
  }, [query, libraryId]);

  const handleResultClick = useCallback((result: SemanticSearchResult) => {
    navigate(`/pages/${result.libraryId}/${result.pageId}`);
    onClose?.();
  }, [navigate, onClose]);

  if (!query.trim()) {
    return <></>;
  }

  if (loading) {
    return (
      <div className="p-3" role="status" aria-live="polite">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Searching by meaning...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3" role="alert">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="p-3">
        <p className="text-sm text-muted-foreground">
          No matching content found. Try different search terms.
        </p>
      </div>
    );
  }

  return (
    <section aria-label="Semantic search results" className="flex flex-col">
      <p className="px-3 py-2 text-xs text-muted-foreground border-b">
        {results.length} result{results.length !== 1 ? 's' : ''} by similarity
      </p>
      <ul className="divide-y" role="list" aria-label="Semantic search results list">
        {results.map((result) => (
          <li key={`${result.pageId}-${result.unitId}`}>
            <button
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-3 py-2.5 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium truncate">{result.pageTitle}</p>
                <span className="ml-2 text-xs font-mono text-muted-foreground shrink-0">
                  {Math.round(result.similarity * 100)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {result.libraryName}
              </p>
              {result.snippet && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {result.snippet}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
